import React, { useEffect, useState, useMemo } from 'react'
import { useCsvDataLoader }                    from './hooks/useCsvDataLoader'
import DashboardControls                       from './dashboard_controls'
import DashboardRenderer                       from './dashboard_renderer'
import { buildWidgetsFromTemplates }           from './widget_helpers'

const ExportViewer = ({ 
  exportId,
  exportModel, 
  exportTitle, 
  csvUrl, 
  encoding, 
  visibleDashboards,
  visibleTables = [],
  showDashboard,
  currency,
  hideDashboardButton,
  userCanRead = false,
  userCanSave = false,
  userIsAdmin = false,
  widgetTemplates = []
}) => {
  const { csvLoading, error, columns, rows }      = useCsvDataLoader(csvUrl, encoding)
  const [datatableLoading,  setDatatableLoading]  = useState(false)
  const [dashboards,        setDashboards]        = useState(visibleDashboards)
  const [currentDashboard,  setCurrentDashboard]  = useState(null)
  const [originalDashboard, setOriginalDashboard] = useState(null)
  const [dashboardView,     setDashboardView]     = useState(showDashboard)
  const [tables,            setTables]            = useState(visibleTables)
  const [currentTable,      setCurrentTable]      = useState(null)
  const [originalTable,     setOriginalTable]     = useState(null)
  const availableWidgetDefinitions = useMemo(() => {
    if (!widgetTemplates) return []
    
    return buildWidgetsFromTemplates(widgetTemplates, currency) 
  }, [widgetTemplates, currency])
  
  useEffect(() => {
    const pathParts = window.location.pathname.split('/')
    const segment   = pathParts[pathParts.length - 2]
    const id        = pathParts[pathParts.length - 1]

    if (segment === 'export_dashboards' && id) {
      const dashboard = dashboards.find(d => d.id == id)
      setCurrentDashboard(dashboard)
      setOriginalDashboard(dashboard)
    }
  }, [dashboards])

  useEffect(() => {
    const pathParts = window.location.pathname.split('/')
    const segment   = pathParts[pathParts.length - 2]
    const id        = pathParts[pathParts.length - 1]

    if (segment === 'export_tables' && id) {
      const table = tables.find(t => t.id == id)
      if (table) {
        setCurrentTable(table)
        setOriginalTable(table)
      }
    }
  }, [tables])

  const isDashboardDirty = useMemo(() => {
    if (!currentDashboard || !originalDashboard) return false
    return JSON.stringify(currentDashboard) !== JSON.stringify(originalDashboard)
  }, [currentDashboard, originalDashboard])

  const isTableDirty = useMemo(() => {
    if (!currentTable || !originalTable) return false
    return JSON.stringify(currentTable) !== JSON.stringify(originalTable)
  }, [currentTable, originalTable])

  if (csvLoading) return <div className="loading"><div className="dot-elastic"></div></div>
  if (error) return <div className="h-60vh d-flex a-i-center j-c-center">{error}</div>
  if (columns.length === 0) return <div className="h-60vh d-flex a-i-center j-c-center">{I18n.t('export.no_data')}</div>

  return (
    <>
      <DashboardControls
        exportId                   = {exportId}
        exportTitle                = {exportTitle}
        dashboards                 = {dashboards}
        widgetTemplates            = {widgetTemplates}
        exportModel                = {exportModel}
        setDashboards              = {setDashboards}
        currentDashboard           = {currentDashboard}
        setCurrentDashboard        = {setCurrentDashboard}
        originalDashboard          = {originalDashboard}
        setOriginalDashboard       = {setOriginalDashboard}
        hideDashboardButton        = {hideDashboardButton}
        userCanRead                = {userCanRead}
        userCanSave                = {userCanSave}
        userIsAdmin                = {userIsAdmin}
        dashboardView              = {dashboardView}
        setDashboardView           = {setDashboardView}
        isDashboardDirty           = {isDashboardDirty}
        columns                    = {columns}
        tables                     = {tables}
        setTables                  = {setTables}
        currentTable               = {currentTable}
        setCurrentTable            = {setCurrentTable}
        originalTable              = {originalTable}
        setOriginalTable           = {setOriginalTable}
        isTableDirty               = {isTableDirty}
      />
      <DashboardRenderer
        key                        = {dashboardView ? 'dashboard' : `table-${currentTable?.id || 'all'}-${currentTable?.columns?.join(',') || ''}`}
        csvLoading                 = {csvLoading}
        datatableLoading           = {datatableLoading}
        columns                    = {currentTable ? currentTable.columns : columns}
        rows                       = {rows}
        availableWidgetDefinitions = {availableWidgetDefinitions} 
        currentDashboard           = {currentDashboard}
        setCurrentDashboard        = {setCurrentDashboard}
        dashboardView              = {dashboardView}
        setDatatableLoading        = {setDatatableLoading}
        exportTitle                = {exportTitle}
        encoding                   = {encoding}
        userCanRead                = {userCanRead}
      />
    </>
  )
}

export default ExportViewer